import { authService } from '@/core/auth/export';
import { LocalStorageUtil } from '@/shared/storage/export';
import { Permissions } from '@/core/permissions/permissions';

const localStorage = new LocalStorageUtil();
const STORAGE_KEY = 'effective-permissions-override';

export type EffectivePermissionsMode = 'production' | 'preproduction';

export interface EffectivePermissionsOverride {
  mode: EffectivePermissionsMode;
  disabledAlphaPermissions: string[];
}

// Pre-production with nothing disabled is the untouched default — it shows
// every real permission, identical to not having an override at all.
const DEFAULT_OVERRIDE: EffectivePermissionsOverride = {
  mode: 'preproduction',
  disabledAlphaPermissions: [],
};

function loadOverride(): EffectivePermissionsOverride {
  const stored = localStorage.getValue<EffectivePermissionsOverride>(STORAGE_KEY);

  if (!stored?.mode) return DEFAULT_OVERRIDE;

  return { mode: stored.mode, disabledAlphaPermissions: stored.disabledAlphaPermissions || [] };
}

let override: EffectivePermissionsOverride = loadOverride();
const listeners = new Set<() => void>();

function persist() {
  localStorage.saveValue(STORAGE_KEY, override);
}

function notify() {
  listeners.forEach(listener => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

function getOverride(): EffectivePermissionsOverride {
  return override;
}

function setMode(mode: EffectivePermissionsMode) {
  override = { ...override, mode };
  persist();
  notify();
}

function clearOverride() {
  override = DEFAULT_OVERRIDE;
  persist();
  notify();
}

authService.onLogout(clearOverride);
authService.onPermissionsChanged(notify);

function setAlphaPermissionEnabled(permission: string, enabled: boolean) {
  if (override.mode !== 'preproduction') return;

  const disabled = new Set(override.disabledAlphaPermissions);

  if (enabled) disabled.delete(permission);
  else disabled.add(permission);

  override = { ...override, disabledAlphaPermissions: [...disabled] };
  persist();
  notify();
}

function computeEffective(real: string[], ov: EffectivePermissionsOverride): string[] {
  if (ov.mode === 'production') return real.filter(p => !Permissions.ANY_ALPHA_OR_PETRO.test(p)); // !Permissions.ANY_ALPHA_ACCESS.test(p)

  return real.filter(
    p => !Permissions.ANY_ALPHA_OR_PETRO.test(p) || !ov.disabledAlphaPermissions.includes(p), // p => !Permissions.ANY_ALPHA_ACCESS.test(p)
  );
}

// authService.getCredentials() re-parses localStorage on every call, returning
// a fresh array reference each time even when nothing changed. useSyncExternalStore
// requires a stable reference across calls when the underlying value hasn't
// changed, so the result is cached and keyed off the actual values involved.
let lastRealKey: string | undefined;
let lastOverrideKey: string | undefined;
let cachedSnapshot: string[] = [];

function getEffectivePermissions(): string[] {
  const real = authService.getCredentials()?.body.permissions || [];
  const realKey = real.join('|');
  const overrideKey = `${override.mode}|${override.disabledAlphaPermissions.join('|')}`;

  if (realKey === lastRealKey && overrideKey === lastOverrideKey) return cachedSnapshot;

  lastRealKey = realKey;
  lastOverrideKey = overrideKey;
  cachedSnapshot = computeEffective(real, override);

  return cachedSnapshot;
}

function getRealAlphaPermissions(): string[] {
  const real = authService.getCredentials()?.body.permissions || [];

  return real.filter(p => Permissions.ANY_ALPHA_OR_PETRO.test(p)); // Permissions.ANY_ALPHA_ACCESS.test(p));
}

const effectivePermissionsService = {
  subscribe,
  getOverride,
  setMode,
  setAlphaPermissionEnabled,
  getEffectivePermissions,
  getRealAlphaPermissions,
};

export default effectivePermissionsService;
